import { sortBy } from "lodash";
import React, { useCallback, useMemo } from "react";

import { getTermAggregationBuckets } from "@examspark/common";
import { useQueriesGetObjectiveScopePaths } from "@examspark/common-app";
import { Text, SelectInput } from "@examspark/common-ui";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";

import { Filter, FilterData } from "../Browse";
import { useSearchFn, useSearch } from "../useSearch";

import FilterObjectivesStandards from "./FilterObjectives.Standards";

type Props = {
  filters: FilterData;
  onChange: (value?: {
    objectiveScope?: string;
    objectiveIds?: string[];
  }) => void;
  useSearchFn?: useSearchFn;
};

const FilterObjectives = ({ filters, onChange, useSearchFn }: Props) => {
  const filter = useMemo(() => {
    const { objectiveScope, objectiveIds, ...filter } = filters;
    return filter;
  }, [filters]);

  const debouncedFilter = useDebounce(filter, 500);

  const searchFn = useSearchFn ?? useSearch;

  const { data } = searchFn({
    queryKey: ["aggs", "objectives-scopes", debouncedFilter],
    searchLabel: "SearchAggsObjectivesScopes",
    limit: 0,
    filter: debouncedFilter,
  });

  const scopePaths = useMemo(() => {
    if (data?.aggregations == null) return undefined;

    return getTermAggregationBuckets(data.aggregations, "objectiveScope");
  }, [data?.aggregations]);

  const scopePathQueries = useQueriesGetObjectiveScopePaths(
    scopePaths?.map((s) => s.key),
  );

  const scopeOptions = useMemo(
    () =>
      sortBy(
        scopePathQueries.map((scopePathQuery, i) => {
          if (
            !scopePathQuery.documentQuery.isSuccess ||
            !scopePathQuery.sectionQuery.isSuccess
          )
            return {
              label: scopePaths?.[i].key ?? "",
              value: scopePaths?.[i].key ?? "",
            };

          return {
            label: `${scopePathQuery.documentQuery.data?.content} ${scopePathQuery.sectionQuery.data?.content}`,
            value: scopePaths?.[i].key ?? "",
            //
            // Note: documents don't have a sequence (in our database)
            //
            documentOrder: scopePathQuery.documentQuery.data?.content,
            sectionOrder: scopePathQuery.sectionQuery.data?.sequence,
          };
        }),
        ["documentOrder", "sectionOrder"],
      ),
    // .sort((a, b) => a.label.localeCompare(b.label)),
    [scopePathQueries, scopePaths],
  );

  const onScopeChange = useCallback(
    (scope: string | undefined) => {
      if (scope === "") scope = undefined;

      onChange({
        objectiveScope: scope,
        objectiveIds: undefined,
      });
    },
    [onChange],
  );

  const onObjectivesChange = useCallback(
    (objectiveIds: string[] | undefined) => {
      if (!objectiveIds?.length) {
        onChange({
          objectiveScope: filters.objectiveScope,
          objectiveIds: undefined,
        });
      } else {
        onChange({
          objectiveScope: filters.objectiveScope,
          objectiveIds: objectiveIds,
        });
      }
    },
    [filters.objectiveScope, onChange],
  );

  return (
    <Filter>
      <Text.H2>Standards</Text.H2>
      <SelectInput
        options={scopeOptions}
        placeholder="Select Scope..."
        value={filters?.objectiveScope}
        onChange={(e) => onScopeChange(e?.value)}
        isClearable={true}
      />
      <FilterObjectivesStandards
        filters={filters}
        onChange={onObjectivesChange}
      />
    </Filter>
  );
};

export default FilterObjectives;
